export type UserRole = 'admin' | 'kurier' | 'servicecenter';

export interface User {
  id: string;
  email: string;
  name: string;
  password: string;
  role: UserRole;
  createdAt: string;
}

// Erlaubte Statuswechsel pro Rolle: [vonStatus, nachStatus]
export type SkiPairStatus =
  | 'Gebucht'
  | 'abgeholt'
  | 'im Service Center'
  | 'bereit für Retournierung'
  | 'in Retournierung'
  | 'Retourniert';

export const ALLOWED_TRANSITIONS: Record<UserRole, Array<[SkiPairStatus, SkiPairStatus]>> = {
  kurier: [
    ['Gebucht', 'abgeholt'],
    ['bereit für Retournierung', 'in Retournierung'],
    ['in Retournierung', 'Retourniert'],
  ],
  servicecenter: [
    ['abgeholt', 'im Service Center'],
    ['im Service Center', 'bereit für Retournierung'],
  ],
  admin: [
    ['Gebucht', 'abgeholt'],
    ['abgeholt', 'im Service Center'],
    ['im Service Center', 'bereit für Retournierung'],
    ['bereit für Retournierung', 'in Retournierung'],
    ['in Retournierung', 'Retourniert'],
  ],
};

/** Gibt alle erlaubten Ziel-Status für einen gegebenen aktuellen Status und eine Rolle zurück */
export const getAllowedNextStatuses = (
  currentStatus: SkiPairStatus,
  role: UserRole
): SkiPairStatus[] => {
  return ALLOWED_TRANSITIONS[role]
    .filter(([from]) => from === currentStatus)
    .map(([, to]) => to);
};

const STORAGE_KEY = 'ski_service_users';
const SESSION_KEY = 'ski_service_session';

const defaultUsers: User[] = [
  {
    id: '1',
    email: 'admin@skiservice.ch',
    name: 'Admin',
    password: 'skiservice2025',
    role: 'admin',
    createdAt: new Date().toISOString(),
  },
  {
    id: '2',
    email: 'kurier@skiservice.ch',
    name: 'Kurier Demo',
    password: 'kurier2025',
    role: 'kurier',
    createdAt: new Date().toISOString(),
  },
  {
    id: '3',
    email: 'service@skiservice.ch',
    name: 'Service Center Demo',
    password: 'service2025',
    role: 'servicecenter',
    createdAt: new Date().toISOString(),
  },
];

export const getUsers = (): User[] => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    return JSON.parse(stored);
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultUsers));
  return defaultUsers;
};

export const saveUsers = (users: User[]): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
};

export const login = (email: string, password: string): User | null => {
  const users = getUsers();
  const user = users.find(
    (u) => u.email.toLowerCase() === email.toLowerCase() && u.password === password
  );

  if (user) {
    const session = {
      userId: user.id,
      email: user.email,
      name: user.name,
      role: user.role,
      loginTime: new Date().toISOString(),
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    return user;
  }

  return null;
};

export const logout = (): void => {
  sessionStorage.removeItem(SESSION_KEY);
};

export const getCurrentSession = (): {
  userId: string;
  email: string;
  name: string;
  role: UserRole;
  loginTime: string;
} | null => {
  const stored = sessionStorage.getItem(SESSION_KEY);
  if (stored) {
    const session = JSON.parse(stored);
    
    // Im Test-Modus: Test-Rolle überschreibt Session-Rolle
    const testModeRole = localStorage.getItem('ski_service_test_role');
    const isTestMode = localStorage.getItem('ski_service_test_mode') === 'true';
    
    if (isTestMode && testModeRole) {
      return {
        ...session,
        role: testModeRole as UserRole,
      };
    }
    
    return session;
  }
  return null;
};

export const isAuthenticated = (): boolean => {
  return getCurrentSession() !== null;
};

export const getCurrentUser = (): User | null => {
  const session = getCurrentSession();
  if (!session) return null;
  const users = getUsers();
  return users.find((u) => u.id === session.userId) || null;
};

export const getUserById = (id: string): User | null => {
  const users = getUsers();
  return users.find((u) => u.id === id) || null;
};

export const addUser = (userData: Omit<User, 'id' | 'createdAt'>): User => {
  const users = getUsers();
  const newUser: User = {
    ...userData,
    id: Date.now().toString(),
    createdAt: new Date().toISOString(),
  };
  users.push(newUser);
  saveUsers(users);
  return newUser;
};

export const updateUser = (id: string, updates: Partial<Omit<User, 'id' | 'createdAt'>>): User | null => {
  const users = getUsers();
  const index = users.findIndex((u) => u.id === id);
  if (index === -1) return null;
  users[index] = { ...users[index], ...updates };
  saveUsers(users);
  return users[index];
};

export const deleteUser = (id: string): boolean => {
  const users = getUsers();
  const filtered = users.filter((u) => u.id !== id);
  if (filtered.length === users.length) return false;
  saveUsers(filtered);
  return true;
};
